import { Question, StudyCalendarEntry, TriageStats, FormulaRouteResult, User, FormulaReviewItem } from '../types';

export async function fetchCurrentUser(): Promise<User | null> {
  const res = await fetch('/api/auth/me');
  if (!res.ok) return null;
  const data = (await res.json()) as { authenticated: boolean; user: User | null };
  return data.authenticated ? data.user : null;
}

export async function logoutUser(): Promise<void> {
  await fetch('/api/auth/logout', { method: 'POST' });
}

export async function fetchNextQuestion(): Promise<Question | null> {
  const res = await fetch('/api/triage/next');
  if (!res.ok) return null;
  return res.json() as Promise<Question>;
}

export async function submitAttempt(payload: {
  questionId: string;
  timeSpentSeconds: number;
  identifiedTheory: boolean;
  selectedCorrectFormula: boolean;
  failureReason?: string | null;
  userInputs?: string | null;
  userTarget?: string | null;
  userTheory?: string | null;
  userFormula?: string | null;
}): Promise<{ success: boolean; attemptId: string }> {
  const res = await fetch('/api/triage/attempt', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  return res.json() as Promise<{ success: boolean; attemptId: string }>;
}

export async function fetchTriageStats(): Promise<TriageStats> {
  const res = await fetch('/api/triage/stats');
  if (!res.ok) {
    return {
      totalAttempts: 0,
      avgTimeSeconds: 0,
      accuracyRate: 0,
      failureBreakdown: {
        OBSCURED_THEORY: 0,
        FORGOT_FORMULA: 0,
        CALCULATION_ERROR: 0,
        MISREAD_INPUTS: 0,
      },
      subjectCounts: {},
    };
  }
  return res.json() as Promise<TriageStats>;
}

export async function fetchCalendarEntries(date?: string): Promise<StudyCalendarEntry[]> {
  const url = date ? `/api/calendar?date=${encodeURIComponent(date)}` : '/api/calendar';
  const res = await fetch(url);
  if (!res.ok) return [];
  return res.json() as Promise<StudyCalendarEntry[]>;
}

export async function createCalendarEntry(entry: {
  date: string;
  period: 'morning' | 'afternoon' | 'evening';
  focus: string;
  todo: string;
}): Promise<{ success: boolean; id: string }> {
  const res = await fetch('/api/calendar', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(entry),
  });
  if (!res.ok) {
    const err = (await res.json()) as { error?: string };
    throw new Error(err.error || 'Erro ao criar compromisso');
  }
  return res.json() as Promise<{ success: boolean; id: string }>;
}

export async function toggleCalendarEntry(id: string, completed: boolean): Promise<{ success: boolean }> {
  const res = await fetch(`/api/calendar/${id}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ completed }),
  });
  return res.json() as Promise<{ success: boolean }>;
}

export async function deleteCalendarEntry(id: string): Promise<{ success: boolean }> {
  const res = await fetch(`/api/calendar/${id}`, {
    method: 'DELETE',
  });
  return res.json() as Promise<{ success: boolean }>;
}

export async function queryFormulaRoute(knowns: string[], target: string): Promise<FormulaRouteResult> {
  const res = await fetch(`/api/graph/route?knowns=${encodeURIComponent(knowns.join(','))}&target=${encodeURIComponent(target)}`);
  if (!res.ok) {
    return { found: false, target, knowns, chains: [], steps: [] };
  }
  return res.json() as Promise<FormulaRouteResult>;
}

export async function fetchQuestionsByFormulaId(formulaId: string): Promise<Question[]> {
  const res = await fetch(`/api/formulas/${encodeURIComponent(formulaId)}/questions`);
  if (!res.ok) return [];
  return res.json() as Promise<Question[]>;
}

export async function fetchFormulaReviewQueue(): Promise<FormulaReviewItem[]> {
  const res = await fetch('/api/formulas/review-queue');
  if (!res.ok) return [];
  return res.json() as Promise<FormulaReviewItem[]>;
}

export async function submitFormulaForReview(payload: {
  caderno: string;
  questionNumber: number;
  statement: string;
  subject: string;
  extractedRawFormula?: string | null;
  canonicalFormulaAttempt?: string | null;
  reason: string;
}): Promise<{ success: boolean; id: string }> {
  const res = await fetch('/api/formulas/review-queue', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  return res.json() as Promise<{ success: boolean; id: string }>;
}

export async function updateFormulaReviewQueue(
  id: string,
  payload: {
    status: 'RESOLVED' | 'REJECTED';
    assignedFormulaId?: string | null;
  }
): Promise<{ success: boolean }> {
  const res = await fetch(`/api/formulas/review-queue/${id}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  return res.json() as Promise<{ success: boolean }>;
}
